import memoize from 'lodash/memoize'
import type { MemoizedFunction } from 'utils/lodashTypes'

/**
 * Memoize a zero-argument async thunk, caching only *successful* results.
 *
 * `lodash/memoize` caches the returned promise, not the resolved value. For an
 * async thunk that means a failure is cached permanently: every later caller
 * awaits the same rejected promise and the work never runs again. Worse, if
 * the thunk can leave its promise pending (an un-caught `.then` chain inside a
 * hand-rolled `new Promise`), the cached promise never settles at all and every
 * consumer hangs until a page reload.
 *
 * This wrapper drops the memo entry when the thunk rejects, so the next call
 * retries instead of replaying the failure. Successful results stay cached
 * exactly as with plain `memoize`.
 *
 * Signature mirrors `lodash/memoize` so this is a drop-in replacement, and the
 * result stays assignable wherever a lodash-memoized getter is expected.
 */
export function memoizeAsyncSuccess<T>(
  fn: () => Promise<T>
): (() => Promise<T>) & MemoizedFunction {
  const memoized = memoize(async (): Promise<T> => {
    try {
      return await fn()
    } catch (error) {
      // lodash keys a zero-argument memo on `undefined`.
      memoized.cache.delete(undefined)
      throw error
    }
  })
  return memoized
}
